import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { styled } from '@this/constants/themes';
import type { ActivitySelected } from '@this/src/domain/trip/trip_activity_store';
import { TripActivityContext, TripActivityStore } from '@this/src/domain/trip/trip_activity_store';
import { lighten, Paper, Badge } from '@material-ui/core';
import { HeaderWrap, HeaderBody, PageTitle } from '../trip_report/shared/wrappers';

type Props = {
  selected: ActivitySelected;
  tripReportAvailable?: boolean;
  tripReceiptAvailable?: boolean;
};

const TripHeader: React.FC<Props> = ({ selected, tripReportAvailable, tripReceiptAvailable }) => {
  const location = useLocation();
  const store = useMemo(() => new TripActivityStore(location.search), []);
  const activity = store.activity(selected);
  const tripUnread = store.unreadCount('trip');
  const tripReportUnread = store.unreadCount('tripReport');
  const receiptUnread = store.unreadCount('receipt');
  const showTripReportTab = tripReportAvailable === undefined ? true : tripReportAvailable;
  const showTripReceiptTab = tripReceiptAvailable === undefined ? false : tripReceiptAvailable;

  const fetchActivity = useCallback(() => {
    store.getActivity();
  }, []);

  useEffect(() => {
    fetchActivity();
  }, []);

  return (
    <TripActivityContext.Provider value={store}>
      <HeaderWrap>
        <HeaderBody>
          <PageTitle>旅程一覧</PageTitle>
          {activity.length > 0 && (
            <Activity>
              {activity.map((row, i) => (
                <ActivityRow key={i}>
                  {row.unread && <ActivityChip>NEW</ActivityChip>}
                  <p>{row.message}</p>
                </ActivityRow>
              ))}
            </Activity>
          )}
          {(showTripReportTab || showTripReceiptTab) && (
            <Navigations>
              <TabWrapper>
                <Badge badgeContent={tripUnread} color="error" invisible={tripUnread < 1}>
                  <Tab className={selected === 'trip' ? 'selected' : ''} to="/trips">
                    予約した旅程
                    {store.tripCount > 0 && `（${store.tripCount}）`}
                  </Tab>
                </Badge>
              </TabWrapper>
              {showTripReportTab && (
                <TabWrapper>
                  <Badge badgeContent={tripReportUnread} color="error" invisible={tripReportUnread < 1}>
                    <Tab className={selected === 'tripReport' ? 'selected' : ''} to="/trip_report">
                      出張後の報告
                      {store.tripReportCount > 0 && `（${store.tripReportCount}）`}
                    </Tab>
                  </Badge>
                </TabWrapper>
              )}
              {showTripReceiptTab && (
                <TabWrapper>
                  <Badge badgeContent={receiptUnread} color="error" invisible={receiptUnread < 1}>
                    <Tab className={selected === 'receipt' ? 'selected' : ''} to="/trip_receipts">
                      領収書発行
                      {store.receiptCount > 0 && `（${store.receiptCount}）`}
                    </Tab>
                  </Badge>
                </TabWrapper>
              )}
            </Navigations>
          )}
        </HeaderBody>
      </HeaderWrap>
    </TripActivityContext.Provider>
  );
};

const Activity = styled.div`
  display: flex;
  flex-flow: column;
  gap: 16px;
  margin-top: 12px;
`;

const ActivityRow = styled(Paper as any)`
  display: flex;
  gap: 8px;
  padding: 16px;
`;

const ActivityChip = styled.div`
  color: ${props => props.theme.redColor};
`;

const Navigations = styled.div`
  display: flex;
  gap: 0;
  margin-top: 30px;

  @media (max-width: 767px) {
    gap: 0;
    margin-top: 20px;
  }
`;

const Tab = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 50px;
  color: ${props => props.theme.textColor};
  background-color: ${props => lighten(props.theme.grayBgColorLight, 0.5)};
  cursor: pointer;
  white-space: nowrap;
  padding: 0 16px;
  text-decoration: none;
  transition: background-color 0.2s ease;
  border-right: 1px solid ${props => props.theme.grayBorderColor};

  &:last-child {
    border-right: none;
  }

  @media (max-width: 767px) {
    width: 100%;
    font-size: 12px;
    height: 45px;
    padding: 0 8px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background-color: ${props => lighten(props.theme.grayBgColorLight, 0.3)};
  }

  &.selected {
    color: #ffffff;
    background-color: ${props => props.theme.accentBorderColor};
    pointer-events: none;
    border-right-color: ${props => props.theme.accentBorderColor};
  }
`;

const TabWrapper = styled.div`
  display: flex;

  @media (max-width: 767px) {
    flex: 1;
    min-width: 0;
  }

  .MuiBadge-root {
    display: flex;
    width: 100%;
  }

  &:last-child ${Tab} {
    border-right: none;
  }
`;

export default TripHeader;
